import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useExportData } from './useExportData';

interface RegionVersion {
  region_id: number;
  region_name: string;
  latest_published_version?: {
    id: number;
    version_number: string;
  };
}

export function useProviderDownload(providerId: number) {
  const [selectedRegion, setSelectedRegion] = useState<number | null>(null);
  const { exportData, isExporting } = useExportData();

  // Get provider info
  const { data: provider } = useQuery({
    queryKey: ['provider', providerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('providers')
        .select('id, name')
        .eq('id', providerId)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!providerId,
  });

  // Get regions and their latest published versions for this provider
  const { data: regionsData, isLoading } = useQuery({
    queryKey: ['provider-regions-versions', providerId],
    queryFn: async () => {
      // Get all regions for this provider
      const { data: regions, error: regionsError } = await supabase
        .from('regions')
        .select('id, name')
        .eq('provider_id', providerId);

      if (regionsError) throw regionsError;

      if (!regions || regions.length === 0) return [];

      // For each region, get the latest published version
      const regionsWithVersions: RegionVersion[] = [];
      
      for (const region of regions) {
        const { data: versions, error: versionsError } = await supabase
          .from('provider_versions')
          .select('id, version_number, created_at')
          .eq('provider_id', providerId)
          .eq('region_id', region.id)
          .eq('status', 'published')
          .order('created_at', { ascending: false })
          .limit(1);

        if (versionsError) throw versionsError;

        regionsWithVersions.push({
          region_id: region.id,
          region_name: region.name,
          latest_published_version: versions && versions.length > 0 ? {
            id: versions[0].id,
            version_number: versions[0].version_number
          } : undefined
        });
      }

      return regionsWithVersions;
    },
    enabled: !!providerId,
  });

  const handleDownload = (regionId?: number) => {
    if (!provider || !regionsData) return;

    const targetRegion = regionId 
      ? regionsData.find(r => r.region_id === regionId)
      : regionsData[0]; // Use first region if only one

    if (!targetRegion?.latest_published_version) return;

    exportData(
      providerId,
      targetRegion.latest_published_version.id,
      provider.name,
      targetRegion.latest_published_version.version_number
    );
  };

  // Determine download button state
  const availableRegions = regionsData?.filter(r => r.latest_published_version) || [];
  const canDownload = availableRegions.length > 0;
  const isSingleRegion = availableRegions.length === 1;
  const isMultipleRegions = availableRegions.length > 1;

  return {
    provider,
    regionsData: availableRegions,
    isLoading,
    canDownload,
    isSingleRegion,
    isMultipleRegions,
    selectedRegion,
    setSelectedRegion,
    handleDownload,
    isExporting,
  };
}